// core.ts — shared engine base: the quote line model and cross-cutting helpers.
//
// Not a rule theme but the common vocabulary every module reuses, grouped here to avoid duplication.

import { STANDS, FINISHED_PRODUCTS, TRANSFERS, ACCESSORIES } from "../../data";
import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";
import type { Material } from "../../models/catalogue.model";
import type { Stand } from "../../models/stands.model";
import type { Accessory } from "../../models/components.model";

/** Auto-measurement formula of a linked per-metre accessory line. */
export type AutoDimFormula = "perimeter" | "double_perimeter" | "width" | "double_width";

/** Quote line types. The legacy "finished product" regime was absorbed by `STAND`. */
export type QuoteLineType =
  | "PRINT_GF" // standard large-format print
  | "SUR_PRINT" // custom print (total entered manually)
  | "SUR_UNIT" // custom unit item (non-print object)
  | "SVC" // service (generated hourly cutting line)
  | "ACC_ML" // accessory per linear metre
  | "STAND" // stand (includes former finished products)
  | "TRANSFER" // screen-printed transfer (TRS family, 6/11 tiers, rigid PA)
  | "MENUIS_FAB"; // custom joinery / fabrication (unit, negotiated PA required)

/**
 * Quote line — the engine input. Dimensions and quantities may be strings or numbers,
 * coerced by `num`.
 */
export interface QuoteLine {
  type: QuoteLineType;
  /** Stable internal identifier (links, import/export). */
  id?: number;
  /** Internal line number (stable key; the display number may differ). */
  num?: number;
  /** Id of the linked line (manual link or trigger), null or absent when unlinked. */
  linkId?: number | null;
  /** Cross-reference prefix label shown at the top of the production note; remembers which variant to use. */
  linkLabel?: string | null | undefined;
  /** Active auto-measurement formula (linked per-metre line following its parent's format). */
  autoDim?: AutoDimFormula;
  /**
   * Custom-size surcharge: requested final format in mm, on one face's nominal dimensions.
   * Present only on the surcharge child line; its presence is what makes a line a surcharge.
   * The rate ref is derived from the parent and the quantity follows the parent.
   */
  customSizeFinal?: { L: number; H: number };
  /** Sleeve diameter (required for sleeve finishings). */
  diam?: string | number;
  /** Different artwork on each face (production info, no pricing effect). Only when the print is double-sided. */
  differentArtwork?: boolean;
  /** Mirrored print, for reading through the substrate (production info, no pricing effect). */
  mirrored?: boolean;
  /**
   * Excluded from the general discount. Unchecking removes the line from the base: it keeps its
   * full price. At a constant discount amount the base shrinks, so the remaining lines absorb more.
   * Absent or false means the line is in the base.
   */
  excludingGeneralDiscount?: boolean;
  material?: string;
  /** Product ref for stand / finished product / per-metre accessory lines. */
  ref?: string;
  /** Composable stand: effective sub-products of a complete stand line. Absent or equal to the
   *  default composition means the pack regime (stored bundle price); different means the composed
   *  regime (sum of sub-products). Detected by comparison, never by a flag. */
  composition?: string[];
  /** File name (informational free text), not part of the calculation. */
  fileName?: string;
  /** Per-line full-name override: Sellsy PDF text for tier regimes. Empty falls back to the catalogue name. */
  fullName?: string;
  /** Dispatch sort values (zone grouping for packing), per column id. Informational only. */
  dispatch?: Record<string, string>;
  /** Production note (free text), not part of the calculation. */
  prodNote?: string;
  /** Install note (free text), not part of the calculation. */
  installNote?: string;
  print?: string;
  cut?: string;
  lamination?: string;
  finishing1?: string;
  finishing2?: string;
  /** Additional priced finishings. */
  finishing3?: string;
  finishing4?: string;
  finishing5?: string;
  finishing6?: string;
  /** Width in mm. */
  L?: string | number;
  /** Height in mm. */
  H?: string | number;
  /** Selected material format ("L × H" mm); absent means the material's default format. */
  format?: string;
  /** "Delivered in several joined pieces": the box decides; unset, it follows the overflow (a size change unsets it). */
  spliced?: boolean;
  /** "Specific width to order": strictly manual checkbox, production summary only. */
  specificSupply?: boolean;
  /**
   * Delivery date and time for this line, in `datetime-local` format. Only present when the
   * back-planning "several delivery dates" box is checked; otherwise lines follow the quote date.
   * It feeds `sub_lines.date_delivery` when lines are split, rather than being a second source.
   */
  deliveryDate?: string;
  /** Number of files. */
  fileQty?: string | number | undefined;
  /** Copies per file. */
  copiesPerFile?: string | number;
  /** Forced sale price (€/m² or total depending on context); overrides the floors. */
  forcedPv?: string | number;
  /** Propagation group ids set by a PV or negotiated-PA propagation. Two fields, since a line can
   *  belong to a PV group and a PA group at once. Pure traceability metadata, no calculation effect. */
  overrideGroupPvId?: string;
  overrideGroupPaId?: string;
  /** Forced minimum unit price (€/piece); stays a protected floor, unlike the forced price. */
  forcedMinUnitPrice?: string | number;
  /** Composition snapshot taken when forcing, used to detect a stale override. */
  forcedPvSnapshot?: string;
  /** Responsible workshop / subcontractor, used for the supplier PA lookup. */
  workshopResp?: string;
  /** Material supplier (distributor) of the line. Indicative: it no longer triggers the supplied-PA mode. */
  matSupplier?: string;
  /** Brand (manufacturer) of the line, filtered on the chosen supplier. Indicative. */
  brand?: string;
  /** "Material supplied" checkbox: the only manual trigger of the supplied-PA mode. UPZ stock is always supplied. */
  materialSupplied?: boolean;
  /** "MAT UPZ" checkbox. With a material set the displayed state is derived from isUpz; with no
   *  material it alone carries the intent (filters menus to UPZ refs). No compute effect. */
  matUpz?: boolean;
  /** PA €/m² entered by hand (takes priority, never clamped by the floor). On a UPZ line: workshop PA. */
  manualPaM2?: string | number;
  /** Negotiated purchase price of the WHOLE line, typed by hand: the €/m² and €/unit are computed from it. */
  manualPaTotal?: string | number;
  /** Unit PA entered by hand. */
  manualPaU?: string | number;
  /** UPZ material PA — gross €/m² entered by hand (overrides the referential). */
  paMatGross?: string | number;
  /** UPZ waste coefficient mode ("flexible" | "rigid" | "none" | "manual"); absent means the nature default. */
  paMatCoeffMode?: string;
  /** UPZ waste coefficient in % (when paMatCoeffMode is "manual"). */
  paMatCoeffPct?: string | number;
  /** Margin % entered by hand (custom print, service). */
  marginManual?: string | number;
  /** Net total entered by hand (custom print, service). */
  enteredTotal?: string | number;
  /** Triggered per-metre finishing line: follows its parent's tier propagation. */
  isFinishingMl?: boolean;
  /** Service ref (SRV-1705 = hourly cutting). */
  svcRef?: string;
  /** Hourly rate of the service (other than hourly cutting). */
  svcRate?: string | number;
  /** Service hours (fallback when the quantity is not entered). */
  svcHours?: string | number;
  /** Free label of an off-catalogue line. */
  offCatalogue?: string;
  /** Weight kg/m² of a custom print. */
  kgM2?: string | number;
}

/** Per-material admin override: forced sale price and/or minimum unit price. */
export interface ForcedMaterial {
  pv?: string | number;
  minUnitPrice?: string | number;
}

/**
 * Is an optional field filled in? Neither empty string nor null/undefined; an explicit 0 counts as filled.
 *
 * @param v The value to test.
 * @returns True when the value is filled.
 */
export function isFilled(v: unknown): boolean {
  return v !== "" && v != null;
}

/**
 * Coerces a value to a number: strips spaces, converts the decimal comma, then parseFloat; non-numeric gives 0.
 *
 * @param v The value to coerce.
 * @returns The number, or 0 when not numeric.
 */
export function num(v: unknown): number {
  let val: unknown = v;
  if (typeof val === "string") val = val.replace(/\s/g, "").replace(",", ".");
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Rounds to the cent (2 decimals).
 *
 * @param x The value to round.
 * @returns The value rounded to 2 decimals.
 */
export function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/**
 * Rounds to 3 decimals (surface display).
 *
 * @param x The value to round.
 * @returns The value rounded to 3 decimals.
 */
export function round3(x: number): number {
  return Math.round(x * 1000) / 1000;
}

/**
 * Unit surface in m²: width × height / 1e6 (dimensions in mm).
 *
 * @param l A line carrying `L` and `H`.
 * @returns The surface of one piece, in m².
 */
export function unitM2(l: Pick<QuoteLine, "L" | "H">): number {
  return (num(l.L) * num(l.H)) / 1e6;
}

/**
 * Catalogue material by ref (string comparison). The catalogue is injected and carries the prices.
 *
 * @param ref The material ref.
 * @param cat The catalogue to search (the active one by default).
 * @returns The material, or undefined.
 */
export function findMat(ref: string | undefined, cat: Catalogue = ACTIVE_CATALOGUE): Material | undefined {
  if (ref == null) return undefined;
  return cat.materials.find((m) => String(m.ref) === String(ref));
}

/**
 * Tiered item by ref: stand, finished product or transfer. The regime comes from the line type, not the table.
 *
 * @param ref The product ref.
 * @returns The item, or undefined.
 */
export function findStand(ref: string | undefined): Stand | undefined {
  return [...STANDS, ...FINISHED_PRODUCTS, ...TRANSFERS].find((s) => s.ref === ref);
}

/**
 * Per-metre accessory by ref.
 *
 * @param ref The accessory ref.
 * @returns The accessory, or undefined.
 */
export function findAccessory(ref: string | undefined): Accessory | undefined {
  return ACCESSORIES.find((a) => a.ref === ref);
}

/**
 * Composition signature of a line (material + components), compared to detect a stale override.
 *
 * @param l The quote line.
 * @returns The signature string.
 */
export function compositionSnapshot(l: QuoteLine): string {
  return [l.material, l.print, l.cut, l.lamination, l.finishing1, l.finishing2].map((x) => x ?? "").join("|");
}

/**
 * Material used as a finishing on the line (mounting): returns the finishing1/finishing2 ref that
 * matches a catalogue material, else null. Such a material forms its own cumul group.
 *
 * @param l A line carrying `finishing1` and `finishing2`.
 * @param cat The catalogue to search (the active one by default).
 * @returns The matching material ref, or null.
 */
export function materialInFinishing(l: Pick<QuoteLine, "finishing1" | "finishing2">, cat: Catalogue = ACTIVE_CATALOGUE): string | null {
  const c = [l.finishing1, l.finishing2].find(
    (k) => k && cat.materials.some((x) => x.ref === k && x.pv != null),
  );
  return c ?? null;
}
